import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, Send, X, FileText } from "lucide-react";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getSessionId = () => {
  let id = localStorage.getItem("da_chat_session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("da_chat_session", id);
  }
  return id;
};

export default function ChatWidget() {
  const { lang, L } = useLang();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const r = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text, lang }),
      });
      const reader = r.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      let failed = false;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data:")) continue;
          const payload = p.slice(5).trim();
          if (payload === "[DONE]") break;
          try {
            const { delta, error } = JSON.parse(payload);
            if (error) failed = true;
            if (delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + delta };
                return copy;
              });
            }
          } catch { /* partial chunk */ }
        }
      }
      if (failed) throw new Error("stream failed");
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: L({ en: "The assistant is momentarily away from the bench — please try again in a moment.", it: "L'assistente si è allontanato dal banco — riprova tra un attimo." }),
        };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  const draftBrief = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/ai/draft-brief`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, lang }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error();
      navigate("/contact", { state: { prefill: d.brief } });
      setOpen(false);
    } catch {
      /* nothing to draft yet */
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <motion.button
        data-testid="chat-widget-btn"
        onClick={() => setOpen(!open)}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.95 }}
        className="fixed bottom-6 right-6 z-[65] flex h-14 w-14 items-center justify-center rounded-full bg-[#0E3B33] text-[#E0BE5A] shadow-xl"
        aria-label={L({ en: "Atelier assistant", it: "Assistente atelier" })}
      >
        {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            data-testid="chat-panel"
            className="fixed bottom-24 right-6 z-[65] flex h-[480px] w-[min(92vw,380px)] flex-col overflow-hidden rounded-xl border border-[#C9A84C]/40 bg-white shadow-2xl"
          >
            <div className="bg-[#0E3B33] px-5 py-4">
              <div className="font-display text-lg text-[#FBF9F3]">{L({ en: "Atelier Assistant", it: "Assistente Atelier" })}</div>
              <div className="mono-tech text-[#C9A84C]">{L({ en: "Answers about services & ideas", it: "Risponde su servizi e idee" })}</div>
            </div>
            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="chat-messages">
              {messages.length === 0 && (
                <p className="mt-6 px-2 text-center text-sm leading-relaxed text-[#8A8F82]">
                  {L({
                    en: "Describe the piece you're dreaming of — metal, stones, style — and I'll help you shape it into a brief.",
                    it: "Descrivi il gioiello che hai in mente — metallo, pietre, stile — e ti aiuto a trasformarlo in un brief.",
                  })}
                </p>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] whitespace-pre-wrap rounded-lg px-3.5 py-2.5 text-sm leading-relaxed ${
                    m.role === "user" ? "bg-[#0E3B33] text-[#FBF9F3]" : "bg-[#F2EDE3] text-[#1E2B26]"
                  }`}>
                    {m.content}
                    {m.role === "assistant" && busy && i === messages.length - 1 && !m.content && (
                      <span className="inline-block h-3 w-3 animate-pulse rounded-full bg-[#C9A84C]" />
                    )}
                  </div>
                </div>
              ))}
            </div>
            {messages.length >= 2 && (
              <button
                data-testid="chat-draft-brief-btn"
                onClick={draftBrief}
                disabled={busy}
                className="mx-4 mb-2 flex items-center justify-center gap-2 rounded-md border border-[#C9A84C]/50 bg-[#C9A84C]/10 px-3 py-2 text-xs font-medium text-[#8A6D2B] transition-colors hover:bg-[#C9A84C]/20 disabled:opacity-50"
              >
                <FileText className="h-3.5 w-3.5" /> {L({ en: "Turn this chat into my brief", it: "Trasforma la chat nel mio brief" })}
              </button>
            )}
            <form onSubmit={send} className="flex gap-2 border-t border-[#0E3B33]/10 p-3">
              <input
                data-testid="chat-input"
                className="forge-field !py-2 text-sm"
                placeholder={L({ en: "Ask the atelier…", it: "Chiedi all'atelier…" })}
                value={input}
                onChange={(e) => setInput(e.target.value)}
              />
              <button type="submit" disabled={busy || !input.trim()} data-testid="chat-send-btn" className="ember-btn shrink-0 !rounded-lg !px-3.5 !py-2 disabled:opacity-50">
                <Send className="h-4 w-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
